import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfRect,
  PdfPoint,
  PdfBrush,
  PdfColor,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutImageGen } from '../layoutImage';
import { OptsImage } from '../optsImage';
import { IItemCount } from '@educraft/base';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/arrow';
import '../../pdf/draw/image';

/**
 * Ludo - Board game layouts (6 variants)
 *
 * 6 portrait pages, each a different Ludo board variant:
 * V1: Classic 11×11 cross-shaped board (4 players)
 * V2: Compact cross with shorter arms (4 players)
 * V3: Cross with 2×2 home lanes (4 players)
 * V4: Linear 2-player track
 * V5: Circular 4-player board (24 positions)
 * V6: Circular 6-player board (24 positions)
 *
 * Migrated from C# EduFrame.Img10.Generators.Ludo
 */

const enum Direction {
  Left,
  Right,
  Up,
  Down,
}
const enum RType {
  None,
  Circle,
  Line,
}

interface LudoI {
  rect: PdfRect;
  color?: number;
}

interface LudoA {
  rect: PdfRect;
  direction: Direction;
}

interface LudoL {
  items: LudoI[];
  type: RType;
}

export class Ludo implements ILayoutImageGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 10;
  }
  countMax(): number {
    return 10;
  }

  private readonly size = PdfUnit.FromMillimeters(15);

  private readonly colors = [
    PdfColor.fromRgb(255, 0, 0), // Red
    PdfColor.fromRgb(0, 128, 0), // Green
    PdfColor.fromRgb(255, 255, 0), // Yellow
    PdfColor.fromRgb(0, 0, 255), // Blue
    PdfColor.fromRgb(148, 0, 211), // DarkViolet
    PdfColor.fromRgb(0, 0, 0), // Black
  ];

  public async generate(pdf: PdfGen, opts: OptsImage): Promise<void> {
    const pages = Array.from({ length: 6 }, () => pdf.addPage());

    this.v1(pages[0], opts);
    this.v2(pages[1], opts);
    this.v3(pages[2], opts);
    this.v4(pages[3], opts);
    this.v5(pages[4], opts);
    this.v6(pages[5], opts);
  }

  private v1(page: PageGen, opts: OptsImage): void {
    const circle: LudoI[] = [
      { rect: this.pi(0, 4), color: 0 },
      { rect: this.pi(1, 4) },
      { rect: this.pi(2, 4) },
      { rect: this.pi(3, 4) },
      { rect: this.pi(4, 4) },
      { rect: this.pi(4, 3) },
      { rect: this.pi(4, 2) },
      { rect: this.pi(4, 1) },
      { rect: this.pi(4, 0) },
      { rect: this.pi(5, 0) },
      { rect: this.pi(6, 0), color: 1 },
      { rect: this.pi(6, 1) },
      { rect: this.pi(6, 2) },
      { rect: this.pi(6, 3) },
      { rect: this.pi(6, 4) },
      { rect: this.pi(7, 4) },
      { rect: this.pi(8, 4) },
      { rect: this.pi(9, 4) },
      { rect: this.pi(10, 4) },
      { rect: this.pi(10, 5) },
      { rect: this.pi(10, 6), color: 2 },
      { rect: this.pi(9, 6) },
      { rect: this.pi(8, 6) },
      { rect: this.pi(7, 6) },
      { rect: this.pi(6, 6) },
      { rect: this.pi(6, 7) },
      { rect: this.pi(6, 8) },
      { rect: this.pi(6, 9) },
      { rect: this.pi(6, 10) },
      { rect: this.pi(5, 10) },
      { rect: this.pi(4, 10), color: 3 },
      { rect: this.pi(4, 9) },
      { rect: this.pi(4, 8) },
      { rect: this.pi(4, 7) },
      { rect: this.pi(4, 6) },
      { rect: this.pi(3, 6) },
      { rect: this.pi(2, 6) },
      { rect: this.pi(1, 6) },
      { rect: this.pi(0, 6) },
      { rect: this.pi(0, 5) },
    ];

    const start: LudoI[] = [
      { rect: this.pi(0, -1), color: 0 },
      { rect: this.pi(1, -1), color: 0 },
      { rect: this.pi(0, 0), color: 0 },
      { rect: this.pi(1, 0), color: 0 },
      { rect: this.pi(9, -1), color: 1 },
      { rect: this.pi(10, -1), color: 1 },
      { rect: this.pi(9, 0), color: 1 },
      { rect: this.pi(10, 0), color: 1 },
      { rect: this.pi(9, 10), color: 2 },
      { rect: this.pi(10, 10), color: 2 },
      { rect: this.pi(9, 11), color: 2 },
      { rect: this.pi(10, 11), color: 2 },
      { rect: this.pi(0, 10), color: 3 },
      { rect: this.pi(1, 10), color: 3 },
      { rect: this.pi(0, 11), color: 3 },
      { rect: this.pi(1, 11), color: 3 },
    ];

    const l1: LudoI[] = [
      { rect: this.pi(0, 5) },
      { rect: this.pi(1, 5), color: 0 },
      { rect: this.pi(2, 5), color: 0 },
      { rect: this.pi(3, 5), color: 0 },
      { rect: this.pi(4, 5), color: 0 },
    ];

    const l2: LudoI[] = [
      { rect: this.pi(5, 0) },
      { rect: this.pi(5, 1), color: 1 },
      { rect: this.pi(5, 2), color: 1 },
      { rect: this.pi(5, 3), color: 1 },
      { rect: this.pi(5, 4), color: 1 },
    ];

    const l3: LudoI[] = [
      { rect: this.pi(10, 5) },
      { rect: this.pi(9, 5), color: 2 },
      { rect: this.pi(8, 5), color: 2 },
      { rect: this.pi(7, 5), color: 2 },
      { rect: this.pi(6, 5), color: 2 },
    ];

    const l4: LudoI[] = [
      { rect: this.pi(5, 10) },
      { rect: this.pi(5, 9), color: 3 },
      { rect: this.pi(5, 8), color: 3 },
      { rect: this.pi(5, 7), color: 3 },
      { rect: this.pi(5, 6), color: 3 },
    ];

    const arrows: LudoA[] = [
      { rect: this.pi(0, 3), direction: Direction.Right },
      { rect: this.pi(7, 0), direction: Direction.Down },
      { rect: this.pi(10, 7), direction: Direction.Left },
      { rect: this.pi(3, 10), direction: Direction.Up },
    ];

    this.background(page);
    this.draw(
      page,
      opts,
      [
        { items: circle, type: RType.Circle },
        { items: l1, type: RType.Line },
        { items: l2, type: RType.Line },
        { items: l3, type: RType.Line },
        { items: l4, type: RType.Line },
        { items: start, type: RType.None },
      ],
      arrows,
      new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height)
    );
  }

  private v2(page: PageGen, opts: OptsImage): void {
    const circle: LudoI[] = [
      { rect: this.pi(0, 4), color: 0 },
      { rect: this.pi(1, 4) },
      { rect: this.pi(2, 4) },
      { rect: this.pi(2, 3) },
      { rect: this.pi(2, 2) },
      { rect: this.pi(2, 1) },
      { rect: this.pi(2, 0) },
      { rect: this.pi(3, 0) },
      { rect: this.pi(4, 0) },
      { rect: this.pi(5, 0), color: 1 },
      { rect: this.pi(5, 1) },
      { rect: this.pi(5, 2) },
      { rect: this.pi(5, 3) },
      { rect: this.pi(5, 4) },
      { rect: this.pi(6, 4) },
      { rect: this.pi(7, 4) },
      { rect: this.pi(7, 5) },
      { rect: this.pi(7, 6) },
      { rect: this.pi(7, 7), color: 2 },
      { rect: this.pi(6, 7) },
      { rect: this.pi(5, 7) },
      { rect: this.pi(5, 8) },
      { rect: this.pi(5, 9) },
      { rect: this.pi(5, 10) },
      { rect: this.pi(5, 11) },
      { rect: this.pi(4, 11) },
      { rect: this.pi(3, 11) },
      { rect: this.pi(2, 11), color: 3 },
      { rect: this.pi(2, 10) },
      { rect: this.pi(2, 9) },
      { rect: this.pi(2, 8) },
      { rect: this.pi(2, 7) },
      { rect: this.pi(1, 7) },
      { rect: this.pi(0, 7) },
      { rect: this.pi(0, 6) },
      { rect: this.pi(0, 5) },
    ];

    const start: LudoI[] = [
      { rect: this.pi(-1, 0), color: 0 },
      { rect: this.pi(0, 0), color: 0 },
      { rect: this.pi(-1, 1), color: 0 },
      { rect: this.pi(0, 1), color: 0 },
      { rect: this.pi(7, 0), color: 1 },
      { rect: this.pi(8, 0), color: 1 },
      { rect: this.pi(7, 1), color: 1 },
      { rect: this.pi(8, 1), color: 1 },
      { rect: this.pi(7, 10), color: 2 },
      { rect: this.pi(8, 10), color: 2 },
      { rect: this.pi(7, 11), color: 2 },
      { rect: this.pi(8, 11), color: 2 },
      { rect: this.pi(-1, 10), color: 3 },
      { rect: this.pi(0, 10), color: 3 },
      { rect: this.pi(-1, 11), color: 3 },
      { rect: this.pi(0, 11), color: 3 },
    ];

    const l1: LudoI[] = [
      { rect: this.pi(0, 5) },
      { rect: this.pi(1, 5), color: 0 },
      { rect: this.pi(2, 5), color: 0 },
      { rect: this.pi(3, 5), color: 0 },
      { rect: this.pi(4, 5), color: 0 },
    ];

    const l2: LudoI[] = [
      { rect: this.pi(4, 0) },
      { rect: this.pi(4, 1), color: 1 },
      { rect: this.pi(4, 2), color: 1 },
      { rect: this.pi(4, 3), color: 1 },
      { rect: this.pi(4, 4), color: 1 },
    ];

    const l3: LudoI[] = [
      { rect: this.pi(7, 6) },
      { rect: this.pi(6, 6), color: 2 },
      { rect: this.pi(5, 6), color: 2 },
      { rect: this.pi(4, 6), color: 2 },
      { rect: this.pi(3, 6), color: 2 },
    ];

    const l4: LudoI[] = [
      { rect: this.pi(3, 11) },
      { rect: this.pi(3, 10), color: 3 },
      { rect: this.pi(3, 9), color: 3 },
      { rect: this.pi(3, 8), color: 3 },
      { rect: this.pi(3, 7), color: 3 },
    ];

    const arrows: LudoA[] = [
      { rect: this.pi(0, 3), direction: Direction.Right },
      { rect: this.pi(6, 0), direction: Direction.Down },
      { rect: this.pi(7, 8), direction: Direction.Left },
      { rect: this.pi(1, 11), direction: Direction.Up },
    ];

    this.background(page);
    this.draw(
      page,
      opts,
      [
        { items: circle, type: RType.Circle },
        { items: l1, type: RType.Line },
        { items: l2, type: RType.Line },
        { items: l3, type: RType.Line },
        { items: l4, type: RType.Line },
        { items: start, type: RType.None },
      ],
      arrows,
      new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height)
    );
  }

  private v3(page: PageGen, opts: OptsImage): void {
    const circle: LudoI[] = [
      { rect: this.pi(0, 4), color: 0 },
      { rect: this.pi(1, 4) },
      { rect: this.pi(2, 4) },
      { rect: this.pi(2, 3) },
      { rect: this.pi(2, 2) },
      { rect: this.pi(2, 1) },
      { rect: this.pi(2, 0) },
      { rect: this.pi(3, 0) },
      { rect: this.pi(4, 0) },
      { rect: this.pi(5, 0), color: 1 },
      { rect: this.pi(5, 1) },
      { rect: this.pi(5, 2) },
      { rect: this.pi(5, 3) },
      { rect: this.pi(5, 4) },
      { rect: this.pi(6, 4) },
      { rect: this.pi(7, 4) },
      { rect: this.pi(7, 5) },
      { rect: this.pi(7, 6) },
      { rect: this.pi(7, 7), color: 2 },
      { rect: this.pi(6, 7) },
      { rect: this.pi(5, 7) },
      { rect: this.pi(5, 8) },
      { rect: this.pi(5, 9) },
      { rect: this.pi(5, 10) },
      { rect: this.pi(5, 11) },
      { rect: this.pi(4, 11) },
      { rect: this.pi(3, 11) },
      { rect: this.pi(2, 11), color: 3 },
      { rect: this.pi(2, 10) },
      { rect: this.pi(2, 9) },
      { rect: this.pi(2, 8) },
      { rect: this.pi(2, 7) },
      { rect: this.pi(1, 7) },
      { rect: this.pi(0, 7) },
      { rect: this.pi(0, 6) },
      { rect: this.pi(0, 5) },
    ];

    const start: LudoI[] = [
      { rect: this.pi(-1, 0), color: 0 },
      { rect: this.pi(0, 0), color: 0 },
      { rect: this.pi(-1, 1), color: 0 },
      { rect: this.pi(0, 1), color: 0 },
      { rect: this.pi(7, 0), color: 1 },
      { rect: this.pi(8, 0), color: 1 },
      { rect: this.pi(7, 1), color: 1 },
      { rect: this.pi(8, 1), color: 1 },
      { rect: this.pi(7, 10), color: 2 },
      { rect: this.pi(8, 10), color: 2 },
      { rect: this.pi(7, 11), color: 2 },
      { rect: this.pi(8, 11), color: 2 },
      { rect: this.pi(-1, 10), color: 3 },
      { rect: this.pi(0, 10), color: 3 },
      { rect: this.pi(-1, 11), color: 3 },
      { rect: this.pi(0, 11), color: 3 },
    ];

    const l1: LudoI[] = [
      { rect: this.pi(0, 5) },
      { rect: this.pi(1, 5), color: 0 },
      { rect: this.pi(2, 5), color: 0 },
      { rect: this.pi(1, 6), color: 0 },
      { rect: this.pi(2, 6), color: 0 },
    ];

    const l2: LudoI[] = [
      { rect: this.pi(4, 0) },
      { rect: this.pi(3, 1), color: 1 },
      { rect: this.pi(3, 2), color: 1 },
      { rect: this.pi(4, 1), color: 1 },
      { rect: this.pi(4, 2), color: 1 },
    ];

    const l3: LudoI[] = [
      { rect: this.pi(7, 6) },
      { rect: this.pi(6, 5), color: 2 },
      { rect: this.pi(5, 5), color: 2 },
      { rect: this.pi(6, 6), color: 2 },
      { rect: this.pi(5, 6), color: 2 },
    ];

    const l4: LudoI[] = [
      { rect: this.pi(3, 11) },
      { rect: this.pi(3, 10), color: 3 },
      { rect: this.pi(3, 9), color: 3 },
      { rect: this.pi(4, 10), color: 3 },
      { rect: this.pi(4, 9), color: 3 },
    ];

    const arrows: LudoA[] = [
      { rect: this.pi(0, 3), direction: Direction.Right },
      { rect: this.pi(6, 0), direction: Direction.Down },
      { rect: this.pi(7, 8), direction: Direction.Left },
      { rect: this.pi(1, 11), direction: Direction.Up },
    ];

    this.background(page);
    this.draw(
      page,
      opts,
      [
        { items: circle, type: RType.Circle },
        { items: l1, type: RType.None },
        { items: l2, type: RType.None },
        { items: l3, type: RType.None },
        { items: l4, type: RType.None },
        { items: start, type: RType.None },
      ],
      arrows,
      new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height)
    );
  }

  private v4(page: PageGen, opts: OptsImage): void {
    const circle: LudoI[] = [
      { rect: this.pi(-1, -7), color: 0 },
      { rect: this.pi(0, -7) },
      { rect: this.pi(0, -6) },
      { rect: this.pi(0, -5) },
      { rect: this.pi(0, -4) },
      { rect: this.pi(0, -3) },
      { rect: this.pi(0, -2) },
      { rect: this.pi(0, -1) },
      { rect: this.pi(0, 0) },
      { rect: this.pi(0, 1) },
      { rect: this.pi(0, 2) },
      { rect: this.pi(0, 3) },
      { rect: this.pi(0, 4) },
      { rect: this.pi(0, 5) },
      { rect: this.pi(0, 6) },
      { rect: this.pi(0, 7) },
      { rect: this.pi(1, 7), color: 1 },
    ];

    const start: LudoI[] = [
      { rect: this.pi(-4, -7), color: 0 },
      { rect: this.pi(-3, -7), color: 0 },
      { rect: this.pi(-4, -6), color: 0 },
      { rect: this.pi(-3, -6), color: 0 },
      { rect: this.pi(4, 7), color: 1 },
      { rect: this.pi(3, 7), color: 1 },
      { rect: this.pi(4, 6), color: 1 },
      { rect: this.pi(3, 6), color: 1 },
    ];

    const arrows: LudoA[] = [
      { rect: this.pi(1, -7), direction: Direction.Down },
      { rect: this.pi(-1, 7), direction: Direction.Up },
    ];

    this.background(page);
    this.draw(
      page,
      opts,
      [
        { items: circle, type: RType.Line },
        { items: start, type: RType.None },
      ],
      arrows,
      new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height)
    );
  }

  private v5(page: PageGen, opts: OptsImage): void {
    const circle: LudoI[] = [];
    const start: LudoI[] = [];
    const ls: LudoL[] = [];

    const radius = PdfSize.A4.width / 2 - this.size;

    for (let angle = 0; angle < 360; angle += 15) {
      const angle1 = angle + 180;
      const p = new PdfPoint(
        radius * Math.cos((angle1 * Math.PI) / 180),
        radius * Math.sin((angle1 * Math.PI) / 180)
      );

      if (angle % 90 === 0) {
        circle.push({ rect: this.pc(p) });
        const l: LudoI[] = [];
        for (let i = 0; i < 5; i++) {
          const r = radius - i * this.size * 1.25;
          const p1 = new PdfPoint(
            r * Math.cos((angle1 * Math.PI) / 180),
            r * Math.sin((angle1 * Math.PI) / 180)
          );
          l.push({ rect: this.pc(p1), color: Math.floor(angle / 90) });
        }
        ls.push({ items: l, type: RType.Line });
      } else {
        circle.push({ rect: this.pc(p) });
      }
    }

    start.push(
      { rect: this.pi(-5, -7), color: 0 },
      { rect: this.pi(-5, -6), color: 0 },
      { rect: this.pi(-4, -7), color: 0 },
      { rect: this.pi(-4, -6), color: 0 },
      { rect: this.pi(5, -7), color: 1 },
      { rect: this.pi(5, -6), color: 1 },
      { rect: this.pi(4, -7), color: 1 },
      { rect: this.pi(4, -6), color: 1 },
      { rect: this.pi(5, 7), color: 2 },
      { rect: this.pi(5, 6), color: 2 },
      { rect: this.pi(4, 7), color: 2 },
      { rect: this.pi(4, 6), color: 2 },
      { rect: this.pi(-5, 7), color: 3 },
      { rect: this.pi(-5, 6), color: 3 },
      { rect: this.pi(-4, 7), color: 3 },
      { rect: this.pi(-4, 6), color: 3 }
    );

    this.background(page);
    this.draw(
      page,
      opts,
      [{ items: circle, type: RType.Circle }, { items: start, type: RType.None }, ...ls],
      [],
      new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height)
    );
  }

  private v6(page: PageGen, opts: OptsImage): void {
    const circle: LudoI[] = [];
    const start: LudoI[] = [];
    const ls: LudoL[] = [];

    const radius = PdfSize.A4.width / 2 - this.size;

    for (let angle = 0; angle < 360; angle += 15) {
      const angle1 = angle + 180 + 30;
      const p = new PdfPoint(
        radius * Math.cos((angle1 * Math.PI) / 180),
        radius * Math.sin((angle1 * Math.PI) / 180)
      );

      if (angle % 60 === 0) {
        circle.push({ rect: this.pc(p) });
        const l: LudoI[] = [];
        for (let i = 0; i < 5; i++) {
          const r = radius - i * this.size * 1.2;
          const p1 = new PdfPoint(
            r * Math.cos((angle1 * Math.PI) / 180),
            r * Math.sin((angle1 * Math.PI) / 180)
          );
          l.push({ rect: this.pc(p1), color: Math.floor(angle / 60) });
        }
        ls.push({ items: l, type: RType.Line });
      } else {
        circle.push({ rect: this.pc(p) });
      }
    }

    start.push(
      { rect: this.pi(-5, -7), color: 0 },
      { rect: this.pi(-5, -6), color: 0 },
      { rect: this.pi(-4, -7), color: 0 },
      { rect: this.pi(-4, -6), color: 0 },
      { rect: this.pr(-0.5, -7), color: 1 },
      { rect: this.pr(-0.5, -6), color: 1 },
      { rect: this.pr(0.5, -7), color: 1 },
      { rect: this.pr(0.5, -6), color: 1 },
      { rect: this.pi(5, -7), color: 2 },
      { rect: this.pi(5, -6), color: 2 },
      { rect: this.pi(4, -7), color: 2 },
      { rect: this.pi(4, -6), color: 2 },
      { rect: this.pi(5, 7), color: 3 },
      { rect: this.pi(5, 6), color: 3 },
      { rect: this.pi(4, 7), color: 3 },
      { rect: this.pi(4, 6), color: 3 },
      { rect: this.pr(-0.5, 7), color: 4 },
      { rect: this.pr(-0.5, 6), color: 4 },
      { rect: this.pr(0.5, 7), color: 4 },
      { rect: this.pr(0.5, 6), color: 4 },
      { rect: this.pi(-5, 7), color: 5 },
      { rect: this.pi(-5, 6), color: 5 },
      { rect: this.pi(-4, 7), color: 5 },
      { rect: this.pi(-4, 6), color: 5 }
    );

    this.background(page);
    this.draw(
      page,
      opts,
      [{ items: circle, type: RType.Circle }, { items: start, type: RType.None }, ...ls],
      [],
      new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height)
    );
  }

  private pi(x: number, y: number): PdfRect {
    return new PdfRect(
      x * this.size * 1.2 - this.size / 2,
      y * this.size * 1.2 - this.size / 2,
      this.size,
      this.size
    );
  }

  private pr(x: number, y: number): PdfRect {
    return new PdfRect(
      x * this.size * 1.2 - this.size / 2,
      y * this.size * 1.2 - this.size / 2,
      this.size,
      this.size
    );
  }

  private pc(p: PdfPoint): PdfRect {
    return new PdfRect(p.x - this.size / 2, p.y - this.size / 2, this.size, this.size);
  }

  private background(page: PageGen): void {
    const bgBrush = new PdfBrush(PdfColor.fromRgb(255, 255, 220));
    page.drawRectangle(null, bgBrush, new PdfRect(0, 0, PdfSize.A4.width, PdfSize.A4.height));
  }

  private draw(
    page: PageGen,
    opts: OptsImage,
    lists: LudoL[],
    arrows: LudoA[],
    bound: PdfRect
  ): void {
    const allItems = lists.flatMap((l) => l.items);
    const minX = Math.min(...allItems.map((i) => i.rect.x));
    const minY = Math.min(...allItems.map((i) => i.rect.y));
    const maxX = Math.max(...allItems.map((i) => i.rect.x + i.rect.width));
    const maxY = Math.max(...allItems.map((i) => i.rect.y + i.rect.height));
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const dx = bound.centerX - cx;
    const dy = bound.centerY - cy;

    for (const list of lists) {
      const rects = list.items.map((p) => ({
        p,
        rect: new PdfRect(dx + p.rect.x, dy + p.rect.y, p.rect.width, p.rect.height),
      }));

      if (list.type === RType.Circle) {
        page.drawPolygon(
          GUtil.basePen,
          null,
          rects.map((r) => new PdfPoint(r.rect.centerX, r.rect.centerY))
        );
      }

      if (list.type === RType.Line && rects.length >= 2) {
        page.drawLines(
          GUtil.basePen,
          rects.map((r) => new PdfPoint(r.rect.centerX, r.rect.centerY))
        );
      }

      for (const p of rects) {
        let brush: PdfBrush = PdfBrush.White;

        if (p.p.color != null) {
          brush = new PdfBrush(this.colors[p.p.color]);
        }

        page.drawEllipse(null, brush, p.rect);

        if (p.p.color != null && p.p.color < opts.list.length) {
          const s = (p.rect.width * Math.SQRT2) / 2;
          const r = new PdfRect(p.rect.centerX - s / 2, p.rect.centerY - s / 2, s, s);
          page.drawImageCenter(opts.list[p.p.color].image, r);
        }

        page.drawEllipse(GUtil.basePen, null, p.rect);
      }
    }

    for (const arrow of arrows) {
      const rect = new PdfRect(
        dx + arrow.rect.x,
        dy + arrow.rect.y,
        arrow.rect.width,
        arrow.rect.height
      );

      switch (arrow.direction) {
        case Direction.Right:
          page.drawArrowRight(rect, PdfBrush.Black, GUtil.basePen);
          break;
        case Direction.Left:
          page.drawArrowLeft(rect, PdfBrush.Black, GUtil.basePen);
          break;
        case Direction.Up:
          page.drawArrowUp(rect, PdfBrush.Black, GUtil.basePen);
          break;
        case Direction.Down:
          page.drawArrowDown(rect, PdfBrush.Black, GUtil.basePen);
          break;
      }
    }
  }
}
